var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { Box } from '@mui/material';
import React from 'react';
import { ActionButton, convertActionsToMenuItems } from '../ActionButton';
import { useCommonDataTableUiTranslation } from '../hooks';
/**
 * Displays buttons in a single row. If buttons do not fit they are moved into a menu.
 */
export const ResponsiveActionToolbar = (_a) => {
    var { actions, align = 'left', variant = 'text', gap = 1, moreActionsLabel, allActionsLabel, sx } = _a, props = __rest(_a, ["actions", "align", "variant", "gap", "moreActionsLabel", "allActionsLabel", "sx"]);
    const { t } = useCommonDataTableUiTranslation();
    const rootRef = React.useRef(null);
    const hiddenButtonsRef = React.useRef(null);
    const [allActionsWidth, setAllActionsWidth] = React.useState();
    const [allButtonsFit, setAllButtonsFit] = React.useState();
    const [numberOfButtonsThatFit, setNumberOfButtonsThatFit] = React.useState();
    const reflow = React.useCallback(() => {
        const rootDiv = rootRef.current;
        const hiddenButtonsDiv = hiddenButtonsRef.current;
        if (rootDiv && hiddenButtonsDiv) {
            const rootWidth = rootDiv.getBoundingClientRect().width;
            const hiddenButtons = hiddenButtonsDiv.children;
            let allActionsWidth = 0;
            let moreActionsWidth = 0;
            const buttonWidths = [];
            for (let i = 0; i < hiddenButtons.length; i++) {
                const buttonWidth = hiddenButtons[i].getBoundingClientRect().width;
                if (i === 0) {
                    allActionsWidth = buttonWidth;
                }
                else if (i === 1) {
                    moreActionsWidth = buttonWidth;
                }
                else {
                    buttonWidths.push(buttonWidth);
                }
            }
            const widthNeededForAllButtons = buttonWidths.reduce((totalWidth, buttonWidth, index) => totalWidth + buttonWidth + (index > 0 ? gap * 8 : 0), 0);
            const allButtonsFit = widthNeededForAllButtons <= rootWidth;
            if (allButtonsFit) {
                setAllButtonsFit(allButtonsFit);
                setNumberOfButtonsThatFit(buttonWidths.length);
            }
            else {
                let xPosition = moreActionsWidth;
                let numberOfButtonsThatFit = 0;
                for (; numberOfButtonsThatFit < buttonWidths.length; numberOfButtonsThatFit++) {
                    let index = numberOfButtonsThatFit;
                    if (align === 'right') {
                        index = buttonWidths.length - numberOfButtonsThatFit - 1;
                    }
                    const withNeededForButton = gap * 8 + buttonWidths[index];
                    if (xPosition + withNeededForButton > rootWidth) {
                        break;
                    }
                    xPosition += withNeededForButton;
                }
                setAllActionsWidth(allActionsWidth);
                setAllButtonsFit(false);
                setNumberOfButtonsThatFit(numberOfButtonsThatFit);
            }
        }
        // need to add variant to dependencies because button widths depend on it
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [align, variant, gap]);
    React.useLayoutEffect(reflow);
    React.useEffect(() => {
        let resizeObserver;
        const rootDiv = rootRef.current;
        if (rootDiv) {
            resizeObserver = new ResizeObserver(() => {
                window.requestAnimationFrame(reflow);
            });
            resizeObserver.observe(rootDiv);
        }
        return () => {
            if (resizeObserver && rootDiv) {
                resizeObserver.unobserve(rootDiv);
            }
        };
    }, [reflow]);
    return (React.createElement(Box, { ref: rootRef, sx: [{ minWidth: allActionsWidth }, ...(Array.isArray(sx) ? sx : [sx])], "data-test": props['data-test'] },
        React.createElement(Box, { ref: hiddenButtonsRef, sx: { position: 'relative', visibility: 'hidden', pointerEvents: 'none' }, "aria-hidden": true },
            React.createElement(ActionButton, { action: { label: allActionsLabel !== null && allActionsLabel !== void 0 ? allActionsLabel : t('actions'), menuItems: [] }, anchor: align, variant: variant, sx: { position: 'absolute' }, tabIndex: -1 }),
            React.createElement(ActionButton, { action: { label: moreActionsLabel !== null && moreActionsLabel !== void 0 ? moreActionsLabel : t('moreActions'), menuItems: [] }, anchor: align, variant: variant, sx: { position: 'absolute' }, tabIndex: -1 }),
            actions.map((action) => (React.createElement(ActionButton, { key: action.label, action: action, variant: variant, sx: { position: 'absolute' }, tabIndex: -1 })))),
        React.createElement(Box, { sx: { display: 'flex', gap: gap * 8 + 'px', justifyContent: align === 'left' ? 'flex-start' : 'flex-end' } }, allButtonsFit
            ? actions.map((action) => {
                var _a;
                return (React.createElement(ActionButton, { key: action.label, action: action, variant: (_a = action.variant) !== null && _a !== void 0 ? _a : variant, "data-test": action['data-test'] }));
            })
            : numberOfButtonsThatFit != null && (React.createElement(React.Fragment, null,
                align === 'left' && (React.createElement(React.Fragment, null,
                    React.createElement(ActionButton, { action: {
                            label: (numberOfButtonsThatFit !== null && numberOfButtonsThatFit !== void 0 ? numberOfButtonsThatFit : 0) > 0
                                ? (moreActionsLabel !== null && moreActionsLabel !== void 0 ? moreActionsLabel : t('moreActions'))
                                : (allActionsLabel !== null && allActionsLabel !== void 0 ? allActionsLabel : t('actions')),
                            menuItems: convertActionsToMenuItems(actions.slice(numberOfButtonsThatFit)),
                            'data-test': 'actions-button',
                        }, anchor: align, variant: variant }),
                    actions.slice(0, numberOfButtonsThatFit).map((action) => (React.createElement(ActionButton, { key: action.label, action: action, variant: variant }))))),
                align === 'right' && (React.createElement(React.Fragment, null,
                    actions.slice(actions.length - numberOfButtonsThatFit).map((action) => (React.createElement(ActionButton, { key: action.label, action: action, variant: variant }))),
                    React.createElement(ActionButton, { action: {
                            label: numberOfButtonsThatFit > 0
                                ? (moreActionsLabel !== null && moreActionsLabel !== void 0 ? moreActionsLabel : t('moreActions'))
                                : (allActionsLabel !== null && allActionsLabel !== void 0 ? allActionsLabel : t('actions')),
                            menuItems: convertActionsToMenuItems(actions.slice(0, actions.length - numberOfButtonsThatFit)),
                            'data-test': 'action-buttons',
                        }, anchor: align, variant: variant }))))))));
};
